/**
 * @module utils/html-utils
 * @description Utility functions for HTML manipulation and sanitization
 */

/**
 * Escape HTML special characters to prevent XSS attacks
 * Converts special characters to their HTML entity equivalents
 *
 * @param {string} text - Text to escape
 * @returns {string} HTML-escaped text
 *
 * @example
 * escapeHtml('<script>alert("xss")</script>');
 * // Returns: "&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;"
 *
 * escapeHtml('Text & <tags>');
 * // Returns: "Text &amp; &lt;tags&gt;"
 */
export function escapeHtml(text) {
    if (!text) return '';

    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Unescape HTML entities back to special characters
 *
 * @param {string} html - HTML-escaped text
 * @returns {string} Unescaped text
 *
 * @example
 * unescapeHtml("&lt;script&gt;");  // "<script>"
 * unescapeHtml("Text &amp; tags");  // "Text & tags"
 */
export function unescapeHtml(html) {
    if (!html) return '';

    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent;
}

/**
 * Strip all HTML tags from a string
 * Removes all HTML tags and returns only text content
 *
 * @param {string} html - HTML string to strip
 * @returns {string} Text without HTML tags
 *
 * @example
 * stripHtmlTags('<p>Hello <strong>world</strong></p>');  // "Hello world"
 */
export function stripHtmlTags(html) {
    if (!html) return '';

    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
}

/**
 * Sanitize HTML by allowing only safe tags
 * Removes potentially dangerous HTML elements and attributes
 *
 * @param {string} html - HTML string to sanitize
 * @param {string[]} allowedTags - Array of allowed HTML tag names (default: ['b', 'i', 'em', 'strong', 'a', 'p', 'br'])
 * @returns {string} Sanitized HTML
 *
 * @example
 * sanitizeHtml('<script>alert("xss")</script><p>Safe text</p>');
 * // Returns: "<p>Safe text</p>"
 */
export function sanitizeHtml(html, allowedTags = ['b', 'i', 'em', 'strong', 'a', 'p', 'br']) {
    if (!html) return '';

    const div = document.createElement('div');
    div.innerHTML = html;

    // Remove disallowed tags
    const allElements = div.querySelectorAll('*');
    allElements.forEach(el => {
        if (!allowedTags.includes(el.tagName.toLowerCase())) {
            el.replaceWith(...el.childNodes);
        }
    });

    return div.innerHTML;
}

/**
 * Create a text node safely
 *
 * @param {string} text - Text content
 * @returns {Text} Text node
 */
export function createTextNode(text) {
    return document.createTextNode(text || '');
}

/**
 * Set text content safely without HTML interpretation
 *
 * @param {HTMLElement} element - Target element
 * @param {string} text - Text to set
 */
export function setTextContent(element, text) {
    if (element) {
        element.textContent = text || '';
    }
}
